import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ethers } from 'ethers';
import { toast } from 'sonner';
import { LifeBuoy, RefreshCw, Flame, Wallet, ExternalLink } from 'lucide-react';
import { API } from '../lib/api';
import { networkOf, txUrl } from '../lib/networks';
import { callSeedLiquidity, callWithdrawUnseeded, hasEnoughForSeed, waitForBytecode } from '../lib/lpRecovery';
import { useWallet } from '../context/WalletContext';

// Recovery UI for tokens whose seedLiquidity step never completed.
// - Complete LP seeding: retries seedLiquidity() with 0.01 ETH.
// - Recover as unseeded: 80% burn / 20% creator, permanent.
// Only visible to the creator, on the matching chain.
const LpRecoveryPanel = ({ contractAddress, chainId, creatorAddress, onSeeded }) => {
  const { wallet, connect } = useWallet();
  const [status, setStatus] = useState({ loading: true, lp_seeded: null, pool_address: null });
  const [busy, setBusy] = useState(false);
  const net = networkOf(chainId);

  const refresh = useCallback(async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const { data } = await axios.get(`${API}/tokens/${contractAddress}/lp-status?chain_id=${chainId}`);
      setStatus({ loading: false, lp_seeded: !!data.lp_seeded, pool_address: data.pool_address || null });
      if (data.lp_seeded && onSeeded) onSeeded();
    } catch (_) {
      setStatus({ loading: false, lp_seeded: null, pool_address: null });
    }
  }, [contractAddress, chainId, onSeeded]);

  useEffect(() => { refresh(); }, [refresh]);

  const isCreator = wallet?.address && creatorAddress && wallet.address.toLowerCase() === creatorAddress.toLowerCase();
  const onRightChain = wallet?.chainId === chainId;

  if (status.loading || status.lp_seeded === true || status.lp_seeded === null) return null;

  const doSeed = async () => {
    if (!wallet) { try { await connect('metamask'); } catch (_) { /* dismissed */ } return; }
    if (!onRightChain) { toast.error(`Switch your wallet to ${net.name} first`); return; }
    if (!isCreator) { toast.error('Only the token creator can seed liquidity'); return; }
    setBusy(true);
    try {
      const balCheck = await hasEnoughForSeed({ provider: wallet.provider, address: wallet.address });
      if (!balCheck.ok) {
        toast.error(`Need ~0.012 ETH (0.01 LP + gas). Balance: ${Number(ethers.formatEther(balCheck.balance)).toFixed(5)} ETH`);
        return;
      }
      // Guard against RPC-lag "contract not deployed" errors on retry too.
      const indexed = await waitForBytecode(wallet.provider, contractAddress, { timeoutMs: 15000 });
      if (!indexed) { toast.error('RPC still catching up. Try again in ~10s.'); return; }
      const signer = await wallet.provider.getSigner();
      const { hash } = await callSeedLiquidity({ signer, contractAddress, weth: net.weth, nfpm: net.nfpm });
      toast.success('LP seeded ✅', {
        description: 'Your pool is live on Uniswap V3.',
        action: { label: 'View tx', onClick: () => window.open(txUrl(chainId, hash), '_blank', 'noopener,noreferrer') },
      });
      await refresh();
    } catch (e) {
      console.error('[coinforge] retry seedLiquidity failed:', e);
      toast.error(e?.shortMessage || e?.message || 'LP seeding failed');
    } finally {
      setBusy(false);
    }
  };

  const doWithdraw = async () => {
    if (!wallet) { try { await connect('metamask'); } catch (_) { /* dismissed */ } return; }
    if (!onRightChain) { toast.error(`Switch your wallet to ${net.name} first`); return; }
    if (!isCreator) { toast.error('Only the token creator can recover'); return; }
    const ok = window.confirm(
      'Recover as unseeded?\n\n• 80% of supply will be BURNED to 0x…dEaD (permanent).\n• 20% will be sent to your wallet.\n• No liquidity pool will ever exist.\n\nThis is irreversible.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const signer = await wallet.provider.getSigner();
      const { hash } = await callWithdrawUnseeded({ signer, contractAddress });
      toast.success('Recovered — 80% burned, 20% sent to you', {
        action: { label: 'View tx', onClick: () => window.open(txUrl(chainId, hash), '_blank', 'noopener,noreferrer') },
      });
      await refresh();
    } catch (e) {
      console.error('[coinforge] withdrawUnseeded failed:', e);
      toast.error(e?.shortMessage || e?.message || 'Recovery failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="lp-recovery-panel"
      className="cf-card p-5 border-[#FBBF24]/40 bg-gradient-to-br from-[#FBBF24]/10 via-transparent to-[#F472B6]/8"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FBBF24]/15 border border-[#FBBF24]/40 flex items-center justify-center shrink-0">
          <LifeBuoy className="w-5 h-5 text-[#FBBF24]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display font-semibold text-white">LP seeding didn&apos;t complete</p>
          <p className="text-forge-muted text-sm mt-1">
            Your token is deployed but the Uniswap V3 pool was never created — 100% of supply is still locked inside the contract.
            Choose an action below to finish (or abandon) the launch. Both options are creator-only.
          </p>

          {!wallet && (
            <p data-testid="lp-recovery-connect-hint" className="text-xs text-[#FBBF24] mt-3 flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" /> Connect your creator wallet to enable these actions.
            </p>
          )}
          {wallet && !isCreator && (
            <p className="text-xs text-[#FF6B7A] mt-3">Connected wallet is not the creator of this token.</p>
          )}
          {wallet && isCreator && !onRightChain && (
            <p className="text-xs text-[#FBBF24] mt-3">Switch your wallet to {net.name} to continue.</p>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              data-testid="lp-recovery-seed-btn"
              onClick={doSeed}
              disabled={busy}
              className="cf-btn-primary text-sm !py-2"
            >
              <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
              Complete LP seeding · 0.01 ETH
            </button>
            <button
              type="button"
              data-testid="lp-recovery-withdraw-btn"
              onClick={doWithdraw}
              disabled={busy}
              className="cf-btn-secondary text-sm !py-2"
            >
              <Flame className="w-4 h-4" />
              Recover as unseeded · 80% burn / 20% to you
            </button>
            <button
              type="button"
              data-testid="lp-recovery-refresh"
              onClick={refresh}
              className="cf-btn-ghost text-sm !py-2"
              title="Re-check on-chain status"
            >
              <ExternalLink className="w-4 h-4" /> Recheck
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LpRecoveryPanel;
